import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, Popper, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store';
import {
  getRecentSearches,
  getResult,
  getVIMSSearchResult,
  clearResults,
  updateRecentFlag,
} from '../../store/search/actions';
import makeStyles from './styles';
import { Result } from '../../store/search/types';
import DeviceLabel from './components/device-label';
import DeviceList from './components/device-list';
import RecentList from './components/recent-list';
import InputField from './components/input-field';
import { option } from './types';
import ErrorMessage from './components/error-message';
import { useClickOutside } from '../../globals/hooks/use-click-outside';
import SearchIcon from '../../assets/icons/search-icon';

const SearchBar = (properties: {
  page?: string;
  closeCallback?: Function;
  selectDeviceCallback?: Function;
}) => {
  const search = useRef<any>();
  const popper = useRef<any>();
  const { page, closeCallback, selectDeviceCallback } = properties;
  const dispatch = useDispatch();
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);

  let recentSearches = useSelector((state: AppState) => state.search.recent);
  const activeTrueArray = recentSearches.filter(({ isFavourite }) => isFavourite);
  const activeFalseArray = recentSearches.filter(({ isFavourite }) => !isFavourite);
  activeTrueArray.sort((a, b) => b.timeStamp - a.timeStamp);
  activeFalseArray.sort((a, b) => b.timeStamp - a.timeStamp);
  recentSearches = activeTrueArray.concat(activeFalseArray);

  const authLoaded: boolean = useSelector((state: AppState) => !!state.login.client);
  const results: Result[] = useSelector((state: AppState) => state.search.result);
  const hasError: boolean = useSelector((state: AppState) => state.search.hasError);
  const deviceInfoError: boolean = useSelector((state: AppState) => state.search.deviceInfoError);
  const deviceNotFoundError: boolean = useSelector((state: AppState) => state.search.deviceNotFoundError);
  const deviceForbiddenError: boolean = useSelector((state: AppState) => state.search.deviceForbiddenError);
  const internalServerError: boolean = useSelector((state: AppState) => state.search.internalServerError);
const gatewayTimeoutError: boolean = useSelector((state: AppState) => state.search.gatewayTimeoutError);
const networkFailureError: boolean = useSelector((state: AppState) => state.search.networkFailureError);
  const updateRecent: boolean = useSelector((state: AppState) => state.search.updateRecent);
  const classes = makeStyles();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    if (authLoaded && updateRecent) {
      dispatch(getRecentSearches());
    }
    dispatch(clearResults());
    setOpen(false);
  }, [authLoaded, dispatch]);

  const hasResults = results.length > 0;
  const devicesList = !!results && !!results[0] && results[0].response && results[0].response.devices;
  let isDealer = false;
  if (hasResults) {
    results.forEach((entity) => {
      if (entity.dealer) {
        isDealer = true;
      }
    });
  }

  const optionsSwitch = (): option => {
    const resultsWithSummary: any = results.map((equipmentInstance) => {
      const instance = {
        ...equipmentInstance,
        header: true,
        summaryString: `${equipmentInstance.deviceId}-${equipmentInstance.moduleId}-${equipmentInstance.moduleId}-${equipmentInstance.deviceKey}-${equipmentInstance.telematicsAssetId}-${equipmentInstance.assetSerialNumber}`,
      };
      return instance;
    });
    if (hasResults) return resultsWithSummary;
    if (inputValue) return [];
    return recentSearches;
  };
  const getOptionLabelSwitch = (instance: any) => {
    if (hasResults) return instance.summaryString;
    if (instance.value) return JSON.stringify(instance);
    return inputValue;
  };

  useEffect(() => {
    if (selectedEntity && !hasError && !deviceInfoError && !deviceForbiddenError && !deviceNotFoundError) {
      setInputValue('');
    }
  }, [selectedEntity, hasError, deviceInfoError, deviceForbiddenError , deviceNotFoundError]);

  const closeSearch = () => {
    setOpen(false);
    dispatch(clearResults());
    setInputValue('');
    if (closeCallback) closeCallback();
  };

  const getRenderOptions = (instance: any) => {
    if (hasResults) {
      return (
        <DeviceList
          page={page}
          selectDeviceCallback={selectDeviceCallback}
          option={instance}
          clearSearch={() => {
            closeSearch();
            dispatch(clearResults());
          }}
        />
      );
    }
    if (inputValue) return null;
    let objectValue = {};
    try {
      objectValue = JSON.parse(instance.key);
    } catch (error) {
      console.log(error);
    }
    return <RecentList option={objectValue} page={page} />;
  };

  useClickOutside([search], () => {
    if (!open) closeSearch();
  });
  useClickOutside([popper], (event: any) => {
    if (!search.current.contains(event.target) && !hasError) closeSearch();
  });

  const CustomPopperContainer = (popperProperties: any) => (
    <Popper
      ref={popper}
      {...popperProperties}
      className={classes.popper}
      placement="bottom-start"
    />
  );

  const performSearch = (criteria: string) => {
    setInputValue(criteria.trim());
    if (page === 'vims-transactions') {
      dispatch(getVIMSSearchResult(criteria.trim()));
    } else {
      dispatch(getResult(criteria.trim()));
    }
    dispatch(clearResults());
  };
  return (
    <div ref={search} className={classes.container}>
      <IconButton
        className={classes.searchIcon}
        onClick={() => {
          if (inputValue.trim()) {
            performSearch(inputValue);
            dispatch(updateRecentFlag(true));
          }
        }}
      >
        <SearchIcon />
      </IconButton>
      <Autocomplete
        PopperComponent={CustomPopperContainer}
        id="telematicSearch"
        freeSolo
        autoHighlight
        blurOnSelect
        disableListWrap
        className={classes.search}
        open={open}
        groupBy={(instance: any) => instance.header}
        renderGroup={(instance: any) => (
          <DeviceLabel key={instance} option={instance} dealer={isDealer} />
        )}
        inputValue={inputValue}
        onInputChange={(event: any, newInputValue: string) => {
          if (event.keyCode === 13) {
            const inputString = newInputValue.trim();
            if (page === 'vims-transactions') {
              dispatch(getVIMSSearchResult(inputString));
            } else {
              dispatch(getResult(inputString));
            }
            setInputValue(inputString);
          } else {
            setInputValue(newInputValue);
          }
        }}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          const options = optionsSwitch();
          if (options[0] && options[0].deviceId) {
            setOpen(false);
          }
        }}
        getOptionLabel={getOptionLabelSwitch}
        options={optionsSwitch()}
        filterOptions={() => optionsSwitch()}
        size="medium"
        renderInput={(parameters) => (
          <InputField
            useFocus={!selectDeviceCallback}
            parameters={parameters}
            performSearch={(criteria: string) => {
              if (criteria.trim()) {
                performSearch(criteria);
              }
            }}
            clearSearch={() => {
              dispatch(clearResults());
            }}
            updateRecentSearches={() => {
              dispatch(updateRecentFlag(true));
            }}
          />
        )}
        renderOption={getRenderOptions}
      />
      <ErrorMessage
        open={hasError || deviceInfoError || deviceForbiddenError ||deviceNotFoundError || internalServerError || gatewayTimeoutError || networkFailureError}
        element={search.current?.lastElementChild}
        deviceInfoError={deviceInfoError}
        deviceForbiddenError={deviceForbiddenError}
        internalServerError={internalServerError}
        gatewayTimeoutError={gatewayTimeoutError}
        networkFailureError={networkFailureError}
        deviceNotFoundError={deviceNotFoundError}
        devicesList={devicesList?.length > 0}
        errorMessage={(page === 'vims-transactions') ? (!devicesList) ? "There are devices attached to this asset. For any such asset, please search it's VIMS transactions in transaction page" : undefined : undefined}
      />
    </div>
  );
};

export default SearchBar;
